<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Percent class="w-5 h-5 text-brand-600" />
            {{ form.id ? 'Editar Cargo/Comisión' : 'Nuevo Cargo/Comisión' }}
          </h2>
          <p class="text-xs text-slate-500">Configuración de la tarifa a cobrar.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="p-6 overflow-y-auto">
        <form id="chargeForm" @submit.prevent="handleSubmit" novalidate class="space-y-6">

          <div>
            <label :class="labelClasses">Nombre del Cargo</label>
            <input v-model="form.name" type="text" placeholder="Ej: Comisión por Desembolso" required :class="inputClasses" />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label :class="labelClasses">Tipo de Cobro</label>
              <select v-model="form.charge_type" :class="inputClasses">
                <option value="PERCENTAGE">Porcentual (%)</option>
                <option value="FIXED">Monto Fijo ($)</option>
              </select>
            </div>

            <div>
              <label :class="labelClasses">Valor Unitario</label>
              <div class="relative">
                <span v-if="form.charge_type === 'FIXED'" class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                <span v-if="form.charge_type === 'PERCENTAGE'" class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">%</span>
                <input
                  v-model.number="form.value"
                  type="number"
                  step="0.01"
                  required
                  :class="[inputClasses, 'font-mono font-bold', form.charge_type === 'FIXED' ? 'pl-8' : 'pr-8']"
                />
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl mt-4">
            <div>
              <h4 class="text-sm font-bold text-slate-900 dark:text-slate-100">Estado del Cargo</h4>
              <p class="text-xs text-slate-500">Permitir que este cargo sea asignado</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" v-model="form.isActive" class="sr-only peer">
              <div :class="switchClasses"></div>
            </label>
          </div>

        </form>
      </div>

      <div class="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
        <button @click="$emit('close')" type="button" :class="btnSecondary">Cancelar</button>
        <button type="submit" form="chargeForm" :disabled="isSaving" :class="btnPrimary">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          <Save v-else class="w-4 h-4" />
          {{ form.id ? 'Guardar Cambios' : 'Crear Cargo' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import { X, Save, Loader2, Percent } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const props = defineProps({ show: Boolean, chargeToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const isSaving = ref(false);

// ESTILOS EN CONSTANTES
const inputClasses = "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all text-slate-700 dark:text-slate-200";
const labelClasses = "text-[10px] font-bold text-slate-500 uppercase mb-1 block";
const switchClasses = "w-11 h-6 bg-slate-300 rounded-full peer dark:bg-slate-700 peer-checked:bg-emerald-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white";
const btnPrimary = "px-6 py-2.5 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto";
const btnSecondary = "px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors w-full sm:w-auto";

const form = reactive({
  id: null,
  name: '',
  charge_type: 'PERCENTAGE',
  value: 0,
  isActive: true // Mapeado internamente a 'ACTIVE' / 'INACTIVE'
});

watch(() => props.show, (val) => {
  if (val) {
    if (props.chargeToEdit && Object.keys(props.chargeToEdit).length > 0) {
      const data = props.chargeToEdit;
      form.id = data.id;
      form.name = data.name || '';
      form.charge_type = data.charge_type || 'PERCENTAGE';
      form.value = data.value || 0;
      form.isActive = data.status !== 'INACTIVE';
    } else {
      Object.assign(form, {
        id: null, name: '', charge_type: 'PERCENTAGE', value: 0, isActive: true
      });
    }
  }
});

const handleSubmit = async () => {
  if (!form.name || form.value === '' || form.value < 0) {
    return showError('Debe proporcionar un nombre válido y un valor mayor o igual a cero.');
  }

  isSaving.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');

    // Preparar el payload
    const payload = {
      id: form.id,
      company_id: user.company,
      name: form.name,
      charge_type: form.charge_type,
      value: form.value,
      status: form.isActive ? 'ACTIVE' : 'INACTIVE'
    };

    // Prevenir el "Column id cannot be null"
    if (!payload.id) delete payload.id;

    if (form.id) {
      await apiClient.put(`/charges_and_fees/${form.id}`, payload);
      showSuccess('Cargo actualizado correctamente.');
    } else {
      await apiClient.post('/charges_and_fees', payload);
      showSuccess('Cargo creado correctamente.');
    }

    emit('saved');
    emit('close');
  } catch (error) {
    showError(error.response?.data?.message || 'Error al guardar el cargo.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
</style>